import React from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Mail } from 'lucide-react';

const Footer = () => {
  return (
    <footer className="bg-brand-primary text-white py-20" data-testid="main-footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
          {/* Brand */}
          <div className="col-span-1 md:col-span-2">
            <div className="flex items-center space-x-3 mb-6">
              <BookOpen className="h-8 w-8 text-brand-accent" />
              <span className="font-heading font-semibold text-2xl tracking-tight">
                Restore My Word
              </span>
            </div>
            <p className="font-body text-slate-300 leading-relaxed mb-6">
              Empowering Gen Zers to become mighty men and women of faith through Bible-based stories that reveal God's reality, power, and transforming love.
            </p>
            <div className="flex space-x-4">
              <a 
                href="https://www.facebook.com/share/1Lfn9mxycs/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-slate-300 hover:text-brand-accent transition-colors duration-300" 
                data-testid="footer-facebook"
                aria-label="Facebook"
              >
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </a>
              <a 
                href="https://youtube.com/@darelldyal?si=tZEmTkt8Y3H3-Dzc" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-slate-300 hover:text-brand-accent transition-colors duration-300" 
                data-testid="footer-youtube"
                aria-label="YouTube"
              >
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
              </a>
              <a 
                href="https://www.tiktok.com/@darelldyal0?_r=1&_t=ZS-97TxdCrZyfoadd" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-slate-300 hover:text-brand-accent transition-colors duration-300" 
                data-testid="footer-tiktok"
                aria-label="TikTok"
              >
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading font-semibold text-lg mb-4 text-brand-accent">Quick Links</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/books" className="font-body text-slate-300 hover:text-brand-accent transition-colors duration-300">
                  Our Books
                </Link>
              </li>
              <li>
                <Link to="/videos" className="font-body text-slate-300 hover:text-brand-accent transition-colors duration-300">
                  Videos
                </Link>
              </li>
              <li>
                <Link to="/daily-word" className="font-body text-slate-300 hover:text-brand-accent transition-colors duration-300">
                  Daily Word
                </Link>
              </li>
              <li>
                <Link to="/faith-teachings" className="font-body text-slate-300 hover:text-brand-accent transition-colors duration-300">
                  Faith Teachings
                </Link>
              </li>
              <li>
                <Link to="/library/story" className="font-body text-slate-300 hover:text-brand-accent transition-colors duration-300">
                  Story Library
                </Link>
              </li>
              <li>
                <Link to="/library/audio" className="font-body text-slate-300 hover:text-brand-accent transition-colors duration-300">
                  Audio Library
                </Link>
              </li>
              <li>
                <Link to="/about" className="font-body text-slate-300 hover:text-brand-accent transition-colors duration-300">
                  About Us
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-heading font-semibold text-lg mb-4 text-brand-accent">Contact</h4>
            <ul className="space-y-3">
              <li className="flex items-center space-x-2">
                <Mail className="h-4 w-4 text-brand-accent" />
                <span className="font-body text-slate-300 text-sm">info@restoremyword.net</span>
              </li>
              <li>
                <Link to="/contact" className="font-body text-slate-300 hover:text-brand-accent transition-colors duration-300">
                  Contact Form
                </Link>
              </li>
              <li>
                <Link to="/donate" className="font-body text-slate-300 hover:text-brand-accent transition-colors duration-300">
                  Support Our Mission
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-slate-700 pt-8 flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
          <p className="font-body text-slate-400 text-sm">
            © {new Date().getFullYear()} RestoreMyWord.net. All rights reserved.
          </p>
          <div className="flex space-x-6">
            <Link to="/privacy" className="font-body text-slate-400 text-sm hover:text-brand-accent transition-colors duration-300">
              Privacy Policy
            </Link>
            <Link to="/terms" className="font-body text-slate-400 text-sm hover:text-brand-accent transition-colors duration-300">
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;